import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '@/lib/api';

const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const [currentSession, setCurrentSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSessions = async () => {
    try {
      const res = await api.get(`/api/academic/sessions`);
      setSessions(res.data || []);
      
      // Find current session
      const current = res.data?.find(s => s.is_current);
      setCurrentSession(current || null);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setSessions([]);
      setCurrentSession(null);
    } finally {
      setLoading(false);
    }
  };

  const setActiveSession = async (sessionId) => {
    try {
      await api.post(`/api/academic/sessions/${sessionId}/set-current`, {});
      await fetchSessions();
      return true;
    } catch (error) {
      console.error('Failed to set session:', error);
      return false;
    }
  };

  const checkSessionRequired = () => {
    // If still loading, allow (will recheck after load)
    if (loading) {
      return { allowed: true, message: '' };
    }
    if (!currentSession) {
      return {
        allowed: false,
        message: 'Please set an Academic Session first. Go to Settings → Academic Year to configure.'
      };
    }
    return { allowed: true, message: '' };
  };

  // Fetch sessions on mount and when auth changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchSessions();
    } else {
      setLoading(false);
    }
  }, []);

  // Re-fetch periodically to catch login
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token && !currentSession && !loading) {
        fetchSessions();
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [currentSession, loading]);

  // Re-fetch when storage changes (for other tabs)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem('token');
      if (token) {
        fetchSessions();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return (
    <SessionContext.Provider value={{
      currentSession,
      sessions,
      loading,
      fetchSessions,
      setActiveSession,
      checkSessionRequired
    }}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContext;
